// confidence — how sure the reader was, in one vocabulary.
//
// Five lanes read documents with AI, and every one of them stages a per-field
// confidence map alongside the reading. The tiers, the dot colors and the dot
// itself live here because a reviewer learns "green means it was sure" once.
// This used to sit mid-file in Procurement/poImportApi.ts, which homed a shared
// vocabulary in one lane's contract module; that lane re-exports it from here.
//
// Kept SEPARATE from grounding.ts on purpose. Confidence is what the model
// reported about its own reading; the gate next door is what a person still
// owes. A low tier blocks nothing by itself — it only says where to look first.

import React from 'react';

export type ConfidenceTier = 'high' | 'medium' | 'low';

/**
 * The mean of a per-field confidence map, or 1 when there is nothing to
 * average.
 *
 * No map is not the same as nobody being sure: an xlsx reading carries none at
 * all, so "no opinion" has to read as "no doubt" or every spreadsheet would
 * wear a red dot.
 *
 * Typed `object` rather than Record<string, number> deliberately: each lane's
 * map is an INTERFACE (ImportConfidence, QuoteConfidence, CatalogImport-
 * Confidence, PackingListConfidence), and TypeScript grants an implicit index
 * signature to type aliases only — the narrower type would refuse all four.
 */
export function averageConfidence(confidence: object | null | undefined): number {
  if (!confidence) return 1;
  const values = Object.values(confidence) as number[];
  if (!values.length) return 1;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function confidenceTier(avg: number): ConfidenceTier {
  if (avg >= 0.85) return 'high';
  if (avg >= 0.6) return 'medium';
  return 'low';
}

export const CONFIDENCE_DOT_COLORS: Record<ConfidenceTier, string> = {
  high: 'bg-green-400',
  medium: 'bg-amber-400',
  low: 'bg-red-400',
};

/**
 * One document's reading confidence, as a dot beside its name.
 *
 * Three colors and a title rather than a printed percentage: the figure only
 * matters while somebody decides how hard to look at this document, and at that
 * moment they are already pointing at it. A number on every row is a number
 * nobody acts on, taking room from the ones they do.
 *
 * Render it only where a model actually did the reading. On a lane whose
 * documents all come from AI that is every row; on one that also takes
 * spreadsheets, guard it (isAiSource).
 */
export const ConfidenceDot: React.FC<{
  confidence: object | null | undefined;
  /** Appended to the dot's own classes, for a row that aligns differently. */
  className?: string;
}> = ({ confidence, className }) => {
  const avg = averageConfidence(confidence);
  return (
    <span
      title={`AI-read confidence: ${Math.round(avg * 100)}%`}
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${
        CONFIDENCE_DOT_COLORS[confidenceTier(avg)]
      }${className ? ` ${className}` : ''}`}
    />
  );
};

export default ConfidenceDot;
